/**
 * AlertsPanel – embeddable card listing active alerts for the current
 * screen, plus a global header bell with badge count.
 *
 * Use anywhere:
 *   <AlertsPanel />                  // full card on a page
 *   <AlertsBell />                   // header dropdown (compact)
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  X,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@/modules/shared/components/ui/badge';
import { Button } from '@/modules/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/modules/shared/components/ui/dropdown-menu';
import { useInsights } from '@/modules/shared/hooks/useInsights';
import {
  alertEngine,
  severityLabel,
  type AlertEvent,
  type AlertSeverity,
} from '@/modules/shared/services/AlertEngineService';
import { highlightSelector } from '@/modules/shared/services/uiHighlight';
import { cn } from '@/lib/utils';
import { formatWorkflowAction } from '@/modules/shared/utils/formatWorkflowAction';

// ──────────────────────────────────────────────────────────────────────
// Severity → visual mapping
// ──────────────────────────────────────────────────────────────────────

const SEVERITY_ICON: Record<AlertSeverity, LucideIcon> = {
  CRITICAL: AlertTriangle,
  HIGH: AlertCircle,
  MEDIUM: AlertCircle,
  LOW: Info,
  INFO: Info,
};

const SEVERITY_STYLES: Record<AlertSeverity, { ring: string; badge: string; icon: string }> = {
  CRITICAL: {
    ring: 'border-l-destructive',
    badge: 'bg-destructive/10 text-destructive border-destructive/30',
    icon: 'text-destructive',
  },
  HIGH: {
    ring: 'border-l-warning',
    badge: 'bg-warning/10 text-warning border-warning/30',
    icon: 'text-warning',
  },
  MEDIUM: {
    ring: 'border-l-warning/60',
    badge: 'bg-warning/10 text-warning border-warning/20',
    icon: 'text-warning',
  },
  LOW: {
    ring: 'border-l-primary',
    badge: 'bg-primary/10 text-primary border-primary/30',
    icon: 'text-primary',
  },
  INFO: {
    ring: 'border-l-border',
    badge: 'bg-muted text-muted-foreground border-border',
    icon: 'text-muted-foreground',
  },
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

// ──────────────────────────────────────────────────────────────────────
// Embeddable card
// ──────────────────────────────────────────────────────────────────────

export interface AlertsPanelProps {
  maxItems?: number;
  hideWhenEmpty?: boolean;
  compact?: boolean;
  className?: string;
}

export function AlertsPanel({
  maxItems = 5,
  hideWhenEmpty = false,
  compact = false,
  className,
}: AlertsPanelProps) {
  const { alerts } = useInsights();
  const items = alerts.slice(0, maxItems);

  if (hideWhenEmpty && items.length === 0) return null;

  return (
    <section
      className={cn(
        'rounded-lg border border-border bg-surface-card',
        compact ? 'p-3' : 'p-4',
        className,
      )}
      data-section-name="alerts"
    >
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bell className="w-3.5 h-3.5 text-primary" />
          <h3 className="text-[14px] font-semibold text-foreground">Active alerts</h3>
        </div>
        {items.length > 0 && (
          <Badge variant="outline" className="text-[11px] py-0 px-1.5 h-4 border-border">
            {items.length}
          </Badge>
        )}
      </header>

      {items.length === 0 ? (
        <p className="text-[12px] text-muted-foreground inline-flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-success" /> No active alerts.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((alert) => (
            <AlertRow key={alert.id} alert={alert} />
          ))}
        </ul>
      )}
    </section>
  );
}

function AlertRow({ alert }: { alert: AlertEvent }) {
  const Icon = SEVERITY_ICON[alert.severity];
  const styles = SEVERITY_STYLES[alert.severity];
  const handleClick = alert.target ? () => highlightSelector(alert.target) : undefined;

  return (
    <li
      className={cn(
        'border border-border border-l-4 rounded-md bg-surface-muted/40 px-3 py-2',
        styles.ring,
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className={cn('w-3.5 h-3.5 mt-0.5 flex-shrink-0', styles.icon)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] font-medium text-foreground">{alert.message}</span>
            <Badge
              variant="outline"
              className={cn('text-[11px] py-0 px-1.5 h-4', styles.badge)}
            >
              {severityLabel(alert.severity)}
            </Badge>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {timeAgo(alert.createdAt)} · {formatWorkflowAction(alert.actionType)}
          </p>
        </div>
        {handleClick && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClick}
            className="h-6 px-2 text-[11px] text-primary hover:bg-primary/10 flex-shrink-0"
          >
            Go
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        )}
        <button
          type="button"
          onClick={() => alertEngine.dismiss(alert.id)}
          className="p-1 rounded hover:bg-surface-hover text-muted-foreground flex-shrink-0"
          title="Dismiss"
          aria-label="Dismiss"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </li>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Header bell (global indicator)
// ──────────────────────────────────────────────────────────────────────

export function AlertsBell() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<AlertEvent[]>(() => alertEngine.getActive());

  useEffect(() => alertEngine.subscribe(setAlerts), []);

  const count = alerts.length;
  const top = useMemo(() => alerts.slice(0, 5), [alerts]);
  const hasCritical = alerts.some((a) => a.severity === 'CRITICAL');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative inline-flex items-center justify-center min-h-0 h-8 w-8 rounded-lg text-muted-foreground hover:bg-surface-hover"
          aria-label={`Notifications${count ? ` (${count})` : ''}`}
        >
          <Bell className="w-3.5 h-3.5" />
          {count > 0 && (
            <span
              className={cn(
                'absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full text-[11px] font-semibold flex items-center justify-center px-1',
                hasCritical ? 'bg-destructive text-white' : 'bg-warning text-white',
              )}
            >
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 max-h-[500px] overflow-hidden flex flex-col">
        <div className="px-3 py-2 border-b border-border bg-surface-muted">
          <p className="text-[12px] font-semibold text-foreground">Notifications</p>
          <p className="text-[11px] text-muted-foreground">
            {count === 0
              ? 'You are all caught up.'
              : `${count} active alert${count === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {top.length === 0 ? (
            <p className="text-[12px] text-muted-foreground p-3 text-center">
              No alerts right now.
            </p>
          ) : (
            <ul className="space-y-2">
              {top.map((a) => (
                <AlertRow key={a.id} alert={a} />
              ))}
            </ul>
          )}
        </div>
        {count > top.length && (
          <button
            type="button"
            onClick={() => navigate('/alerts')}
            className="text-[11px] text-primary hover:bg-surface-hover px-3 py-2 border-t border-border text-left"
          >
            View all {count} alerts →
          </button>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
